import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputWithLabel,
  SelectWithLabel,
} from "../../components/micro/input";
import Toggles from "../../components/common/Toggles";
import FileUploadChoose from "../../components/common/FileUploadChoose";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import { createProduct, uploadImage } from "../../redux/product/slice";
import { getCategoriesList } from "../../redux/categories/slice";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/common/BreadCrumb";
import { InputError } from "../../components/error/error";
import RippleLoader from "../../components/loader/RippleLoader";

const CreateEditProduct = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEditMode = location?.state?.mode;
  const [isEditing, setIsEditing] = useState(false);
  const [isToggle, setIsToggle] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [resetForm, setResetForm] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(null);
  const [isDisable, setIsDisable] = useState(true);
  const productData = location?.state?.product;

  const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(file);
      video.muted = true;
      video.playsInline = true;
      video.currentTime = 1; 

      video.addEventListener("loadeddata", () => {
        const canvas = document.createElement("canvas");
        canvas.width = 150;
        canvas.height = 150;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, 150, 150);
        resolve(canvas.toDataURL("image/png"));
      });

      video.addEventListener("error", () => resolve(null));
    });
  };

  const handleFileChange = async (
    event,
    setFieldValue,
    currentUploadedFiles = []
  ) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const tempFiles = await Promise.all(
      files.map(async (file) => {
        const preview = file.type.startsWith("video/")
          ? await generateVideoThumbnail(file) 
          : URL.createObjectURL(file);

        return {
          id: null,
          url: "",
          preview,
          type: file.type,
          name: file.name,
          isLoading: true,
        };
      })
    );

    const newFiles = [...currentUploadedFiles, ...tempFiles];
    setUploadedFiles(newFiles);
    setFieldValue("uploadedFiles", newFiles);

    const formData = new FormData();
    files.forEach((file) => formData.append("file[]", file));
    formData.append("type", "product");

    try {
      const response = await dispatch(uploadImage(formData));
      const uploadedData = response.payload?.data;

      if (
        response.type === "uploadImage/fulfilled" &&
        uploadedData?.length > 0
      ) {
        const uploadedFilesData = uploadedData.map((file, index) => ({
          url: file.url,
          preview: tempFiles[index].preview,
          type: tempFiles[index].type,
          name: file.name,
          id: file.id,
          isLoading: false,
        }));

        const updatedFiles = [...currentUploadedFiles, ...uploadedFilesData];
        setUploadedFiles(updatedFiles);
        setFieldValue("uploadedFiles", updatedFiles);
      } else {
        throw new Error("File upload failed");
      }
    } catch (error) {
      toast.error("File upload failed");
      setUploadedFiles(currentUploadedFiles);
      setFieldValue("uploadedFiles", currentUploadedFiles);
    }
  };

  const handleDelete = (index, setFieldValue) => {
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);

    setUploadedFiles(updatedFiles);
    setFieldValue("uploadedFiles", updatedFiles);
  };

  // selector
  const { allCategoriesList, allCategoriesLoading } = useSelector((store) => ({
    allCategoriesList: store?.categoriesData?.getCategoriesListStatus?.data,
    allCategoriesLoading: store?.categoriesData?.loading,
  }));

  useEffect(() => {
    dispatch(getCategoriesList({ search: "", limit: "all" }));
  }, []);

  const handleConfirm = async () => {
    setIsLoading(true);

    const category_id = selectedCategories?.value
      ? selectedCategories?.value
      : productData?.category_id;

    // Take only current image IDs
    const currentImageIds = uploadedFiles.map((file) => file.id);

    const payload = {
      name: formValues.productName,
      category_id,
      warranty_duration: formValues.warrantyDuration,
      fixed_price: isToggle,
      price: isToggle ? parseFloat(formValues.pricing) : 0,
      description: formValues.productDescription,
      specification: formValues.productSpecification,
      images: currentImageIds, // Only send currently selected images
    };

    const action = productData?.id
      ? createProduct({ id: productData.id, ...payload })
      : createProduct(payload);

    try {
      const res = await dispatch(action);
      if (res.type === "createProduct/fulfilled") {
        navigate("/products");
      }
    } catch (error) {
      console.error("Error creating product:", error);
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditMode === "edit" && productData) {
      setIsToggle(productData.fixed_price === 1);

      if (Array.isArray(productData?.images)) {
        const inferMimeType = (url) => {
          const ext = url?.split(".").pop().toLowerCase();
          if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
            return `image/${ext === "jpg" ? "jpeg" : ext}`;
          if (["mp4", "webm", "ogg"].includes(ext)) return `video/${ext}`;
          return "application/octet-stream";
        };

        const formattedImages = productData.images.map((img) => ({
          url: img?.document?.url,
          type: inferMimeType(img?.document?.url),
          id: img?.document_id,
        }));

        setUploadedFiles(formattedImages);

        setImageUrls(formattedImages.map((img) => img.id));
      }
    }
  }, [isEditMode, productData]);

  return (
    <>
      <div className="mb-3">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Create Product
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbActive={
              isEditMode == "edit" ? "Edit Product" : "Create Product"
            }
            breadCrumbParent2={"Product"}
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
      </div>
      <div className="bg-white shadow-md rounded-lg p-6">
        <h6 className="font-Montserrat font-medium text-xl mb-3">
          {" "}
          {isEditMode == "edit" ? "Edit Product" : "Create Product"}{" "}
        </h6>
        {allCategoriesLoading ? (
          <RippleLoader />
        ) : (
          <Formik
            initialValues={{
              productName: isEditMode === "edit" ? productData?.name || "" : "",
              category:
                isEditMode === "edit" ? productData?.category_id || "" : "",
              warrantyDuration:
                isEditMode === "edit"
                  ? productData?.warranty_duration || ""
                  : "",
              fixed_price:
                isEditMode === "edit" ? !!productData?.fixed_price : false,

              pricing:
                isEditMode === "edit"
                  ? productData?.fixed_price
                    ? productData?.price
                    : ""
                  : "",
              productDescription:
                isEditMode === "edit" ? productData?.description || "" : "",
              productSpecification:
                isEditMode === "edit" ? productData?.specification || "" : "",
              uploadedFiles: productData?.images?.length
                ? productData.images.map((img) => img.document.url)
                : [],
            }}
            validationSchema={Yup.object().shape({
              productName: Yup.string()
                .trim()
                .nullable()
                .required("Please enter name"),
              category: Yup.string()
                .nullable()
                .required("Please select a category"),
              warrantyDuration: Yup.string()
                .nullable()
                .required("Please enter warranty duration"),
              fixed_price: Yup.boolean().required(
                "Please select whether the price is fixed"
              ),
              pricing: isToggle
                ? Yup.number()
                    .required("Please enter price")
                    .min(0, "Price must be a positive number")
                : Yup.number(),
              productDescription: Yup.string()
                .trim()
                .nullable()
                .required("Please enter description"),
              productSpecification: Yup.string()
                .trim()
                .nullable()
                .required("Please enter product specification"),
              uploadedFiles: Yup.array()
                .min(1, "Please upload image")
                .required("Please upload image"),
            })}
            onSubmit={(values, { resetForm }) => {
              setFormValues(values);
              setShowConfirmationModal(true);
              setResetForm(() => resetForm);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-6 mb-6">
                  {/* Product Name */}
                  <InputWithLabel
                    idFromName="productName"
                    label="Product Name"
                    type="text"
                    onChange={(e) => {
                      handleChange(e);
                      setIsDisable(false);
                    }}
                    onBlur={handleBlur}
                    value={values.productName}
                    errors={errors.productName && touched.productName}
                    errorsText={errors.productName}
                    disabled={isEditMode === "edit" && !isEditing}
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black border hover:border-themeBtn"
                    }  ${
                      errors.productName && touched.productName
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                  />
                  {/* Category */}
                  <div>
                    <div className={`plus-number-inputs`}>
                      <SelectWithLabel
                        idFromName="category"
                        placeholder="Category"
                        options={allCategoriesList?.data?.map((category) => ({
                          value: category.id,
                          label: category.name,
                        }))}
                        disabled={isEditMode == "edit" && !isEditing}
                        value={
                          selectedCategories?.value
                            ? selectedCategories?.value
                            : productData?.category_id
                        }
                        onChange={(e) => {
                          const selectedOption = allCategoriesList?.data?.find(
                            (cat) => cat.id === parseInt(e.target.value)
                          );

                          setSelectedCategories({
                            value: selectedOption.id,
                            label: selectedOption.name,
                          });
                          setFieldValue("category", selectedOption.id);
                          setIsDisable(false);
                        }}
                      />
                    </div>
                    {errors.category && touched.category ? (
                      <InputError errorTitle={errors.category} />
                    ) : null}
                  </div>

                  {/* Warranty Duration */}
                  <InputWithLabel
                    idFromName="warrantyDuration"
                    label="Warranty Duration"
                    type="number"
                    onChange={(e) => {
                      handleChange(e);
                      setIsDisable(false);
                    }}
                    onBlur={handleBlur}
                    value={values.warrantyDuration}
                    errors={errors.warrantyDuration && touched.warrantyDuration}
                    errorsText={errors.warrantyDuration}
                    disabled={isEditMode === "edit" && !isEditing}
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "e" ||
                        e.key === "E" ||
                        e.key === "+" ||
                        e.key === "-"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black border hover:border-themeBtn"
                    }  ${
                      errors.warrantyDuration && touched.warrantyDuration
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                  />
                </div>

                {/* Toggle Switch */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="flex items-center mb-6">
                    <label className="flex items-center cursor-pointer">
                      <Toggles
                        checked={isToggle}
                        toggleFor={isToggle}
                        onChange={() => {
                          setIsToggle((prev) => !prev);
                          handleChange({
                            target: { name: "fixed_price", value: !isToggle },
                          });
                        }}
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                      <span className="ml-2 text-base font-Proxima">
                        Switch to on if it's a fixed price item
                      </span>
                    </label>
                  </div>

                  {/* Pricing */}
                  {isToggle && (
                    <InputWithLabel
                      idFromName="pricing"
                      label="Pricing"
                      type="number"
                      onChange={(e) => {
                        handleChange(e);
                        setIsDisable(false);
                      }}
                      onBlur={handleBlur}
                      value={values.pricing}
                      errors={errors.pricing && touched.pricing}
                      errorsText={errors.pricing}
                      disabled={isEditMode === "edit" && !isEditing}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                      className={`${
                        isEditMode === "edit" && !isEditing
                          ? "text-textGrey border border-dashed "
                          : "text-black border hover:border-themeBtn"
                      }  ${
                        errors.pricing && touched.pricing
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn`
                      }  transition duration-300 ease-in-out`}
                    />
                  )}
                </div>

                {/* Product Description */}
                <div className="grid grid-cols-1 gap-6 mb-6">
                  <InputWithLabel
                    idFromName="productDescription"
                    label="Product Description"
                    type="textarea"
                    onChange={(e) => {
                      handleChange(e);
                      setIsDisable(false);
                    }}
                    onBlur={handleBlur}
                    value={values.productDescription}
                    errors={
                      errors.productDescription && touched.productDescription
                    }
                    errorsText={errors.productDescription}
                    disabled={isEditMode === "edit" && !isEditing}
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black border hover:border-themeBtn"
                    }  ${
                      errors.productDescription && touched.productDescription
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    inputClass="additional-input-classes"
                  />
                  <InputWithLabel
                    idFromName="productSpecification"
                    label="Product Specification"
                    type="textarea"
                    onChange={(e) => {
                      handleChange(e);
                      setIsDisable(false);
                    }}
                    onBlur={handleBlur}
                    value={values.productSpecification}
                    errors={
                      errors.productSpecification &&
                      touched.productSpecification
                    }
                    errorsText={errors.productSpecification}
                    disabled={isEditMode === "edit" && !isEditing}
                    className={`${
                      isEditMode === "edit" && !isEditing
                        ? "text-textGrey border border-dashed "
                        : "text-black border hover:border-themeBtn"
                    }  ${
                      errors.productSpecification &&
                      touched.productSpecification
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    }  transition duration-300 ease-in-out`}
                    inputClass="additional-input-classes"
                  />
                </div>

                {/* Product Image Upload */}
                <div className="mb-4 mt-4">
                  <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                    Product Image
                  </label>
                  {isLoading ? (
                    <RippleLoader />
                  ) : (
                    <>
                      <FileUploadChoose
                        className={`border border-borderTheme/80 transition duration-300 ease-in-out ${
                          errors.uploadedFiles && touched.uploadedFiles
                            ? "border-red"
                            : ""
                        }`}
                        disabled={isEditMode === "edit" && !isEditing}
                        handleFileChange={(event) =>
                          handleFileChange(
                            event,
                            setFieldValue,
                            values.uploadedFiles
                          )
                        }
                        uploadedFiles={uploadedFiles}
                        accept="image/*,video/*"
                        onDelete={(index) => handleDelete(index, setFieldValue)}
                      />
                      {errors.uploadedFiles && touched.uploadedFiles ? (
                        <InputError errorTitle={errors.uploadedFiles} />
                      ) : null}
                    </>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value="Cancel"
                    onClick={() => navigate(-1)}
                  />
                  {isEditMode === "edit" ? (
                    isEditing ? (
                      <>
                        <div className="flex gap-2">
                          <DefaultButton type="submit" value="confirm" />
                        </div>
                      </>
                    ) : (
                      <DefaultButton
                        value="Edit"
                        onClick={handleEditClick}
                        type="submit"
                      />
                    )
                  ) : (
                    <DefaultButton
                      type="submit"
                      value="create"
                      disabled={isDisable}
                    />
                  )}
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
      {showConfirmationModal && (
        <div>
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => handleConfirm(resetForm)}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default CreateEditProduct;
